import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { APP_VERSION } from "@/lib/config";
import { SozuLogo } from "@/components/ui/sozu-logo";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useDynamicMenus, DynamicMenuItem } from "@/hooks/useDynamicMenus";
import { useInmobiliariaDataStatus } from "@/hooks/useInmobiliariaDataStatus";
import { esRutaBloqueadaInmobiliaria } from "@/lib/rutasBloqueadasInmobiliaria";
import { prefijoDePortal } from "@/lib/buscadorMenus";
import { agruparHijosMenu, esHijoActivo } from "@/lib/comunicacionGrupos";
import { useAdminVerComo } from "@/contexts/AdminVerComoContext";
import { useVistasDelRol } from "@/hooks/usePortalBancos/useVistasDelRol";
import { usePerfilVista } from "@/hooks/usePerfilVista";
import { useFotoPerfilVista } from "@/hooks/useFotoPerfilVista";
import { AvatarPersona } from "@/components/ui/avatar-persona";
import { useAvatarVista } from "@/hooks/useAvatarVista";
import { UserSettingsDialog } from "./UserSettingsDialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ChevronDown, ChevronRight, LogOut, X, Lock, ExternalLink } from "lucide-react";
 
 interface AdminSidebarProps {
   isOpen: boolean;
   onClose: () => void;
   currentPath: string;
 }
 
export const AdminSidebar = ({ isOpen, onClose, currentPath }: AdminSidebarProps) => {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { menuItems: menuItemsPropios, isLoading: isLoadingMenusPropios, isSuperAdmin } = useDynamicMenus();

  // "Ver como": filtro de PINTADO con las vistas del rol de la persona. La autorizacion sigue con
  // el rol real (useDynamicMenus). Mientras cargan sus vistas no se pinta de mas.
  const { persona } = useAdminVerComo();
  const { data: vistasPersona, isLoading: cargandoVistas } = useVistasDelRol(persona?.rolAccesoId ?? null);
  const perfilVista = usePerfilVista();
  const fotoVista = useFotoPerfilVista();
  // Presencia de la identidad efectiva, la misma lectura que el avatar del encabezado.
  const avatarVista = useAvatarVista();
  const isLoadingMenus = isLoadingMenusPropios || (!!persona && cargandoVistas);
  const menuItems = useMemo(() => {
    if (!persona) return menuItemsPropios;
    const vistas = vistasPersona ?? new Set<string>();
    const ok = (href?: string) => !!href && vistas.has(href);
    return menuItemsPropios.flatMap((item): DynamicMenuItem[] => {
      if (item.isPortal) {
        const visible = ok(item.href) || (item.portalChildren ?? []).some((c) => ok(c.href));
        return visible ? [item] : [];
      }
      if (item.children) {
        const children = item.children.filter((c) => ok(c.href));
        return children.length > 0 ? [{ ...item, children }] : [];
      }
      return ok(item.href) ? [item] : [];
    });
  }, [persona, vistasPersona, menuItemsPropios]);

  // Si la ruta actual no es de la persona, a su primera vista permitida (o al inicio).
  useEffect(() => {
    if (!persona || !vistasPersona || isLoadingMenusPropios) return;
    const permitida = [...vistasPersona].some((v) => currentPath === v || currentPath.startsWith(v + "/"));
    if (permitida || currentPath === "/admin") return;
    const hrefs = menuItems.flatMap((i): string[] => (i.children ? i.children.map((c) => c.href) : i.href ? [i.href] : []));
    const primera = hrefs[0];
    navigate(primera ?? "/admin", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.email, vistasPersona, isLoadingMenusPropios, currentPath]);

  // Check if user is Inmobiliaria role (rol_id 4)
  const isInmobiliariaRole = profile?.rol_id === 4;

  // Fetch inmobiliaria data for Inmobiliaria role users
  // For primary users: id_persona points to the inmobiliaria
  // For secondary users: we need to look up via entidades_relacionadas
  const { data: inmobiliariaData } = useQuery({
    queryKey: ['sidebar-inmobiliaria-data', profile?.rol_id, profile?.id_persona, profile?.email],
    queryFn: async () => {
      if (!profile) return null;

      // First check if user's persona IS the inmobiliaria (primary user)
      if (profile.id_persona) {
        const { data: personaData, error: personaError } = await supabase
          .from('personas')
          .select('id, nombre_legal, nombre_comercial, url_logo, email')
          .eq('id', profile.id_persona)
          .single();

        if (!personaError && personaData) {
          // Check if this persona is an inmobiliaria (tipo_entidad = 5)
          const { data: entidadData } = await supabase
            .from('entidades_relacionadas')
            .select('id')
            .eq('id_persona', profile.id_persona)
            .eq('id_tipo_entidad', 5)
            .eq('activo', true)
            .maybeSingle();

          if (entidadData) {
            // User's persona IS the inmobiliaria
            return {
              nombre_legal: personaData.nombre_legal,
              nombre_comercial: personaData.nombre_comercial,
              logo_url: personaData.url_logo,
              inmobiliaria_id: personaData.id,
            };
          }
        }
      }

      // Secondary user: look up inmobiliaria via proyectos_acceso -> entidades_relacionadas
      if (profile.email) {
        const { data: proyectoAcceso } = await supabase
          .from('proyectos_acceso')
          .select('id_entidad_relacionada_dueno')
          .eq('usuario_id', profile.email)
          .eq('activo', true)
          .not('id_entidad_relacionada_dueno', 'is', null)
          .limit(1)
          .maybeSingle();

        if (proyectoAcceso?.id_entidad_relacionada_dueno) {
          const { data: entidadDuena } = await supabase
            .from('entidades_relacionadas')
            .select('id_persona, personas!fk_entrel_persona(id, nombre_legal, nombre_comercial, url_logo)')
            .eq('id', proyectoAcceso.id_entidad_relacionada_dueno)
            .eq('activo', true)
            .maybeSingle();

          if (entidadDuena?.personas) {
            const inmob = entidadDuena.personas as any;
            return {
              nombre_legal: inmob.nombre_legal,
              nombre_comercial: inmob.nombre_comercial,
              logo_url: inmob.url_logo,
              inmobiliaria_id: inmob.id,
            };
          }
        }
      }

      return null;
    },
    enabled: isInmobiliariaRole && !!profile,
  });

  // Use the hook to check if inmobiliaria data is complete
  const { isDataComplete, missingFields, isLoading: isLoadingDataStatus } = useInmobiliariaDataStatus(
    isInmobiliariaRole ? profile?.id_persona : null
  );

  // Vistas bloqueadas con datos incompletos: misma regla que el buscador de menús.
  const isRouteBlocked = (href: string) =>
    esRutaBloqueadaInmobiliaria(href, { isSuperAdmin, isInmobiliariaRole, isDataComplete });

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth/login");
  };
 
   // Auto-expand the group that contains the current path
   const getInitialExpandedGroups = (items: DynamicMenuItem[]) => {
     const expanded = new Set<string>();
     items.forEach(item => {
       if (item.children) {
         const hijos = agruparHijosMenu(item.menuId, item.children);
         const hasActiveChild = hijos.some(child => esHijoActivo(child, currentPath, hijos));
         if (hasActiveChild) {
           expanded.add(item.title);
         }
       }
     });
     return expanded;
   };
 
   const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [cuentaAbierta, setCuentaAbierta] = useState(false);
 
   // Update expanded groups when menuItems change
   useEffect(() => {
     if (menuItems.length > 0) {
       setExpandedGroups(getInitialExpandedGroups(menuItems));
     }
   }, [menuItems, currentPath]);
 
   const toggleGroup = (groupTitle: string) => {
     const newExpanded = new Set(expandedGroups);
     if (newExpanded.has(groupTitle)) {
       newExpanded.delete(groupTitle);
     } else {
       newExpanded.add(groupTitle);
     }
     setExpandedGroups(newExpanded);
   };
 
   return (
     <>
       {/* Overlay for mobile */}
       {isOpen && (
         <div 
           className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
           onClick={onClose}
         />
       )}
       
       {/* Sidebar */}
       <div className={cn(
         "fixed top-0 left-0 z-50 h-full w-64 bg-sidebar text-sidebar-foreground border-r border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0 flex flex-col",
         isOpen ? "translate-x-0" : "-translate-x-full"
       )}>
         {/* Brand */}
         <div className="flex flex-col gap-1 px-5 pb-5 pt-4 flex-shrink-0">
           <div className="flex items-center justify-between">
             <SozuLogo className="h-6" />
             <button
               onClick={onClose}
               className="lg:hidden p-1 rounded-md hover:bg-accent transition-colors"
             >
               <X className="h-5 w-5" />
             </button>
           </div>
           <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
             {isInmobiliariaRole ? "By Sozu" : "Admin Panel"}
           </p>
         </div>
 
         {/* Navigation - con scroll */}
         <div className="flex-1 overflow-hidden">
           <ScrollArea className="h-full">
           {isLoadingMenus ? (
             <div className="flex items-center justify-center py-8">
               <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
             </div>
           ) : (
             <nav className="py-4 space-y-2 px-4">
               {menuItems.map((item, index) => (
                 <div key={index}>
                   {item.href && item.isPortal ? (
                     <Link
                       to={item.href}
                       className={cn(
                         "flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors font-medium border",
                         item.isRestrictedPortal
                           ? currentPath.startsWith(prefijoDePortal(item.href))
                             ? "bg-blue-600 text-white border-blue-600"
                             : "bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-400 text-blue-700 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/50"
                           : currentPath.startsWith(prefijoDePortal(item.href))
                             ? "bg-primary text-primary-foreground border-primary"
                             : "bg-accent/60 border-border hover:bg-accent hover:border-primary/40 text-foreground"
                       )}
                       onClick={onClose}
                     >
                       <div className="flex items-center space-x-3">
                         <item.icon className="h-5 w-5" />
                         <span>{item.title}</span>
                       </div>
                       <ExternalLink className="h-3.5 w-3.5 opacity-50" />
                     </Link>
                   ) : item.href ? (
                     <Link
                       to={item.href}
                       className={cn(
                         "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors",
                         currentPath === item.href
                           ? "bg-primary text-primary-foreground"
                           : "hover:bg-accent"
                       )}
                       onClick={onClose}
                     >
                       <item.icon className="h-5 w-5" />
                       <span>{item.title}</span>
                     </Link>
                   ) : (
                     <div className="space-y-1">
                       <button
                         onClick={() => toggleGroup(item.title)}
                         className={cn(
                           "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-all border",
                           item.isSoloA
                             ? "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-400 dark:bg-blue-950/40 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/50"
                             : "text-muted-foreground hover:text-foreground hover:bg-accent border-transparent"
                         )}
                       >
                         <div className="flex items-center space-x-3">
                           <item.icon className="h-5 w-5" />
                           <span>{item.title}</span>
                         </div>
                         {expandedGroups.has(item.title) ? (
                           <ChevronDown className="h-4 w-4 transition-transform" />
                         ) : (
                           <ChevronRight className="h-4 w-4 transition-transform" />
                         )}
                       </button>
                       {expandedGroups.has(item.title) && (
                         <div className="space-y-1 animate-in slide-in-from-top-2 duration-200">
                           {agruparHijosMenu(item.menuId, item.children ?? []).map((child, childIndex, hijos) => {
                             const isBlocked = isRouteBlocked(child.href);
                             // Resaltado: match exacto o subruta del submenú, pero solo si
                             // ningún hermano más específico también hace match (ej.
                             // Inventarios → Precios). Las entradas agrupadas de Comunicación
                             // (Avisos, Citas) se resaltan en cualquiera de sus pestañas.
                             const isChildActive = esHijoActivo(child, currentPath, hijos);
                             if (isBlocked) {
                               return (
                                 <TooltipProvider key={childIndex} delayDuration={100}>
                                   <Tooltip>
                                     <TooltipTrigger asChild>
                                       <div className="flex items-center justify-between pl-8 pr-3 py-2 rounded-lg text-sm text-muted-foreground/50 cursor-not-allowed">
                                         <div className="flex items-center space-x-3">
                                           <child.icon className="h-4 w-4" />
                                           <span>{child.title}</span>
                                         </div>
                                         <Lock className="h-3 w-3" />
                                       </div>
                                     </TooltipTrigger>
                                     <TooltipContent side="right" className="max-w-xs">
                                       <p className="font-medium">Sección bloqueada</p>
                                       <p className="text-xs text-muted-foreground mt-1">
                                         Completa la información en "Mi información" para habilitar esta sección.
                                       </p>
                                     </TooltipContent>
                                   </Tooltip>
                                 </TooltipProvider>
                               );
                             }
                             return (
                               <Link
                                 key={childIndex}
                                 to={child.href}
                                 className={cn(
                                   "flex items-center space-x-3 pl-8 pr-3 py-2 rounded-lg transition-colors text-sm",
                                   isChildActive
                                     ? "bg-primary text-primary-foreground"
                                     : "hover:bg-accent"
                                 )}
                                 onClick={onClose}
                               >
                                 <child.icon className="h-4 w-4" />
                                 <span>{child.title}</span>
                               </Link>
                             );
                           })}
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               ))}
             </nav>
           )}
           </ScrollArea>
         </div>
 
         {/* Footer */}
         <div className="space-y-1 px-3 pb-4 pt-3">
           {/* Igual que el pie de PortalSidebar y el avatar del encabezado: abre Mi cuenta. */}
           <button
             type="button"
             onClick={() => setCuentaAbierta(true)}
             className="group/perfil flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted/60"
           >
             <AvatarPersona nombre={perfilVista.nombre} fotoUrl={fotoVista} enLinea={avatarVista.enLinea} ultimaActividad={avatarVista.ultimaActividad} size="sm" />
             <div className="flex-1 text-left min-w-0">
               <p className="text-[13px] font-medium text-foreground truncate">{perfilVista.nombre || "Usuario"}</p>
               <p className="text-[11px] text-muted-foreground truncate">{perfilVista.rol || "Sin rol"}</p>
             </div>
             <ChevronRight className="size-4 text-muted-foreground opacity-0 transition-opacity group-hover/perfil:opacity-100" />
           </button>
           <UserSettingsDialog open={cuentaAbierta} onOpenChange={setCuentaAbierta} />
           <button
             onClick={handleSignOut}
             className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[12px] text-destructive hover:bg-destructive/10 transition-colors"
           >
             <LogOut className="size-4 shrink-0" />
             Cerrar sesión
           </button>
           <p className="text-[10px] text-muted-foreground/40 font-mono text-center pt-0.5">{APP_VERSION}</p>
         </div>
       </div>
     </>
   );
 };
